import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { equipmentAPI } from '../../services/api'
import { trainingLocationsAPI } from '../../services/trainingLocations'
import { contentLanguage } from '../../i18n/supported'

// Predefined gym locations - uses i18n for translations
const GYM_LOCATIONS = [
  'free_weights',
  'machine_area',
  'cardio_area',
  'functional_area',
  'stretching_area',
  'outdoor_area',
  'other'
]

// Helper function to detect if text is likely non-English
// Part of multi-language architecture: warn users to enter English text
function detectNonEnglish(text) {
  if (!text || text.length < 3) return null

  // German indicators
  const germanPatterns = [
    /[äöüßÄÖÜ]/,  // German-specific characters
    /\b(und|oder|mit|für|bei|auf|aus|nach|von|zu|der|die|das|ein|eine|ist|sind|haben|werden|kann|muss|soll)\b/i,  // Common German words
    /\b(maschine|gerät|hantel|stange|bank|kabel|bein|brust|rücken|arm|schulter)\b/i,  // German gym terms
  ]

  // Portuguese indicators
  const portuguesePatterns = [
    /[ãõçÃÕÇáéíóúâêôÁÉÍÓÚÂÊÔ]/,  // Portuguese-specific characters
    /\b(com|para|não|são|está|uma|esse|essa|isso|como|mais|também)\b/i,  // Common Portuguese words
  ]

  // French indicators
  const frenchPatterns = [
    /[àâçéèêëîïôùûüÿœæÀÂÇÉÈÊËÎÏÔÙÛÜŸŒÆ]/,  // French-specific characters
    /\b(avec|pour|dans|sont|cette|aussi|mais|plus|comme|tout)\b/i,  // Common French words
  ]

  for (const pattern of germanPatterns) {
    if (pattern.test(text)) return 'German'
  }
  for (const pattern of portuguesePatterns) {
    if (pattern.test(text)) return 'Portuguese'
  }
  for (const pattern of frenchPatterns) {
    if (pattern.test(text)) return 'French'
  }

  return null
}

// Equipment Form Modal Component
// Following multi-language architecture: English is the base language
// Translations are generated on-demand via the translate endpoint
export function EquipmentFormModal({
  equipment,
  equipmentTypes,
  muscleGroups,
  onClose,
  onSuccess,
  // VH-131: on a location's own page the place is already known, so adding a
  // machine there must not ask again. Null on the All-equipment view, where the
  // user genuinely has to say where it goes.
  defaultLocationId = null,
  // VH-131: what the AI read off the photo. Every field is a starting point,
  // not a decision -- the user corrects whatever it got wrong before saving,
  // which is the difference between this and the scan review's one-tap accept.
  aiPrefill = null,
}) {
  const { t, i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)
  const [languageWarning, setLanguageWarning] = useState(null)

  // VH-126: a machine has to be somewhere, so the form needs the user's places.
  const { data: locationsData } = useQuery({
    queryKey: ['training-locations', 'list'],
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data),
  })
  const trainingLocations = locationsData?.locations || []

  // Form uses English as the base language (name_en -> name, notes_en -> notes)
  // For backwards compatibility, we still send name_de/name_en but use same value
  const [formData, setFormData] = useState({
    name_de: equipment?.name_en || equipment?.name_de
      || aiPrefill?.proposed_name_en || '',  // Use English as primary
    name_en: equipment?.name_en || aiPrefill?.proposed_name_en || '',
    equipment_type: equipment?.equipment_type || aiPrefill?.equipment_type || '',
    available_at_gym: equipment?.available_at_gym ?? true,
    location_in_gym: equipment?.location_in_gym || '',
    video_url: equipment?.video_url || '',
    notes_de: equipment?.notes_en || equipment?.notes_de || '',  // Use English as primary
    notes_en: equipment?.notes_en || '',
    default_weight_notation: equipment?.default_weight_notation || 'single',
    primary_muscle_groups:
      equipment?.primary_muscle_groups || aiPrefill?.primary_muscle_groups || [],
    // VH-126: equipment is owned by a location and the column is NOT NULL,
    // so the API rejects a create without one.
    location_id: equipment?.location_id || defaultLocationId || ''
  })

  // Check for non-English input when form fields change
  useEffect(() => {
    const textToCheck = `${formData.name_en} ${formData.notes_en}`
    const detected = detectNonEnglish(textToCheck)
    setLanguageWarning(detected)
  }, [formData.name_en, formData.notes_en])

  // VH-130: the server refuses a move that would put two same-named machines
  // in one place, unless it is told to go ahead. It is a warning, not a rule --
  // Leg Curl Machine x3 is real -- but a silent duplicate breaks scan adoption,
  // which matches by name and takes the first hit.
  const [duplicateWarning, setDuplicateWarning] = useState(null)

  const mutation = useMutation({
    mutationFn: async ({ data, confirmDuplicate }) => {
      if (equipment) {
        return equipmentAPI.update(equipment.id, data, confirmDuplicate)
      }
      const created = await equipmentAPI.create(data)

      // Carry the AI's crop onto the new machine. Deliberately best-effort:
      // the equipment already exists and is correct, so a failed photo must
      // not present itself as a failed save. The card offers "Add photo" for
      // exactly this case.
      const cropUrl = aiPrefill?.crop_image_url
      if (cropUrl && created?.data?.id) {
        try {
          const blob = await (await fetch(cropUrl)).blob()
          await equipmentAPI.uploadPhoto(
            created.data.id,
            new File([blob], 'scan.jpg', { type: blob.type || 'image/jpeg' })
          )
        } catch {
          // Swallowed on purpose -- see above.
        }
      }
      return created
    },
    onSuccess: onSuccess,
    onError: (error) => {
      if (error?.response?.status === 409) {
        // Keep the user's input; they may still want to proceed.
        setDuplicateWarning(error.response.data?.detail || t('equipment.duplicateNameAtLocation'))
        return
      }
      setDuplicateWarning(null)
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setDuplicateWarning(null)
    mutation.mutate({ data: formData, confirmDuplicate: false })
  }

  const handleConfirmDuplicate = () => {
    setDuplicateWarning(null)
    mutation.mutate({ data: formData, confirmDuplicate: true })
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => {
      const newValue = type === 'checkbox' ? checked : value

      // For name/notes fields, sync both _de and _en for backwards compatibility
      if (name === 'name') {
        return { ...prev, name_de: value, name_en: value }
      }
      if (name === 'notes') {
        return { ...prev, notes_de: value, notes_en: value }
      }

      return { ...prev, [name]: newValue }
    })
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {equipment ? t('equipment.editEquipment') : t('equipment.addEquipment')}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Language Warning Banner */}
          {languageWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    {t('equipment.form.languageWarningTitle', { language: languageWarning })}
                  </h3>
                  <p className="mt-1 text-sm text-amber-700">
                    {t('equipment.form.languageWarningMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Single name field - English as base language, translations generated on-demand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('equipment.form.name')} *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name_en}
              onChange={handleChange}
              placeholder={t('equipment.form.namePlaceholder')}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">{t('equipment.form.nameHint')}</p>
          </div>

          {/* VH-131: what the AI saw. Shown so the user can tell at a glance
              whether the card describes the machine they photographed. */}
          {aiPrefill?.crop_image_url && (
            <div className="flex items-center gap-3 rounded-md bg-primary-50 p-3">
              <img
                src={aiPrefill.crop_image_url}
                alt={aiPrefill.proposed_name_en || ''}
                className="h-16 w-16 rounded object-cover"
                data-testid="ai-prefill-photo"
              />
              <p className="text-xs text-gray-600">
                {t('equipment.addByPhoto.prefilled')}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('trainingLocations.title')} *
            </label>
            <select
              name="location_id"
              required
              value={formData.location_id}
              onChange={handleChange}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
              data-testid="equipment-location-select"
            >
              <option value="">{t('equipment.selectLocation')}</option>
              {trainingLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('equipment.form.equipmentType')} *
            </label>
            <select
              name="equipment_type"
              required
              value={formData.equipment_type}
              onChange={handleChange}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('equipment.selectType')}</option>
              {equipmentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {t(`equipment.types.${type.value}`, type.label)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('equipment.locationInGym')}
            </label>
            <select
              name="location_in_gym"
              value={formData.location_in_gym}
              onChange={handleChange}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('equipment.selectLocation')}</option>
              {GYM_LOCATIONS.map((location) => (
                <option key={location} value={location}>
                  {t(`equipment.locations.${location}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('equipment.videoUrl')}
            </label>
            <input
              type="url"
              name="video_url"
              value={formData.video_url}
              onChange={handleChange}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('equipment.form.weightNotation')}
            </label>
            <select
              name="default_weight_notation"
              value={formData.default_weight_notation}
              onChange={handleChange}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="single">{t('equipment.weightNotations.single')}</option>
              <option value="dumbbell">{t('equipment.weightNotations.dumbbell')}</option>
              <option value="bodyweight">{t('equipment.weightNotations.bodyweight')}</option>
              <option value="assisted">{t('equipment.weightNotations.assisted')}</option>
            </select>
          </div>

          {/* Single notes field - English as base language, translations generated on-demand */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('equipment.form.notes')}
            </label>
            <textarea
              name="notes"
              value={formData.notes_en}
              onChange={handleChange}
              rows="3"
              placeholder={t('equipment.form.notesPlaceholder')}
              className="w-full focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('equipment.form.primaryMuscleGroups')}
            </label>
            <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
              {muscleGroups.map((mg) => (
                <label key={mg.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.primary_muscle_groups.includes(mg.value)}
                    onChange={(e) => {
                      const newGroups = e.target.checked
                        ? [...formData.primary_muscle_groups, mg.value]
                        : formData.primary_muscle_groups.filter(g => g !== mg.value)
                      setFormData(prev => ({
                        ...prev,
                        primary_muscle_groups: newGroups
                      }))
                    }}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    {currentLanguage === 'de' ? mg.label_de : mg.label_en}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="available_at_gym"
              checked={formData.available_at_gym}
              onChange={handleChange}
              className="text-primary-600 focus:ring-primary-500"
            />
            <label className="ml-2 text-sm font-medium text-gray-700">
              {t('equipment.availableAtGym')}
            </label>
          </div>

          {/* VH-130: the duplicate-name warning. Shown instead of a plain
              error, because the move is allowed -- the user just has to mean
              it. Their input is preserved either way. */}
          {duplicateWarning && (
            <div
              className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm"
              data-testid="duplicate-name-warning"
            >
              <p className="text-yellow-800">{duplicateWarning}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={handleConfirmDuplicate}
                  data-testid="confirm-duplicate-move"
                  className="btn-primary text-xs py-1"
                >
                  {t('equipment.moveAnyway')}
                </button>
                <button
                  type="button"
                  onClick={() => setDuplicateWarning(null)}
                  className="btn-outline text-xs py-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t('common.loading') : t('common.save')}
            </button>
          </div>

          {mutation.isError && !duplicateWarning && (
            <div className="text-sm text-red-600 mt-2">
              {t('common.error')}: {mutation.error?.response?.data?.detail || t('equipment.saveFailed')}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default EquipmentFormModal
